import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/InputDefault";
import { Button } from "@/components/ui/button";
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { Archive, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import { apiService } from "@/lib/api-functions/api-service";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ArchiveGameModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameId: string;
  gameName: string;
  companyName: string;
  onGameArchived?: () => void;
}

interface EntityPreview {
  count: number;
  items: { id: string; name: string }[];
}

interface ArchivePreview {
  game: {
    id: string;
    name: string;
    description: string;
  };
  entities: {
    documents: EntityPreview;
    blueprints: EntityPreview;
    libraryItems: EntityPreview;
    dialogs: EntityPreview;
    types: EntityPreview;
    tags: EntityPreview;
    logicFlows: EntityPreview;
    tmsStrings: EntityPreview;
    translations: EntityPreview;
    uiElements: EntityPreview;
    relationships: EntityPreview;
    images: EntityPreview;
  };
  summary: {
    totalEntities: number;
    totalTranslations: number;
    totalRelationships: number;
    totalImages: number;
  };
}

function EntityList({ title, count, items }: { title: string; count: number; items: { id: string; name: string }[] }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (count === 0) {
    return (
      <div className="flex items-center justify-between py-2 px-3 bg-muted/50 rounded">
        <span className="text-sm text-muted-foreground">{title}</span>
        <span className="text-sm font-medium text-muted-foreground">0</span>
      </div>
    );
  }

  return (
    <div className="border rounded overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between py-2 px-3 bg-muted/50 hover:bg-muted transition-colors"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          <span className="text-sm font-medium">{title}</span>
        </div>
        <span className="text-sm font-bold">{count}</span>
      </button>
      {isExpanded && items.length > 0 && (
        <div className="max-h-32 overflow-y-auto border-t">
          {items.map((item) => (
            <div
              key={item.id}
              className="py-1.5 px-4 text-xs text-muted-foreground hover:bg-muted/30 truncate"
              title={item.name}
            >
              {item.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ArchiveGameModal({
  isOpen,
  onClose,
  gameId,
  gameName,
  companyName,
  onGameArchived
}: ArchiveGameModalProps) {
  const { t } = usePlatformTranslation();
  const { toast } = useToast();
  const [archiveSuffix, setArchiveSuffix] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [isArchiving, setIsArchiving] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewData, setPreviewData] = useState<ArchivePreview | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Generate archive name
  const companyNameSafe = companyName.replace(/[^a-zA-Z0-9]/g, '_');
  const gameNameSafe = gameName.replace(/[^a-zA-Z0-9]/g, '_');
  const suffixSafe = archiveSuffix ? `_${archiveSuffix.replace(/[^a-zA-Z0-9]/g, '_')}` : '';
  const archiveName = `${companyNameSafe}_${gameNameSafe}${suffixSafe}`;

  const expectedText = `Archive ${gameName}`;
  const isConfirmValid = confirmText === expectedText;

  useEffect(() => {
    if (isOpen) {
      loadPreview();
      setArchiveSuffix("");
      setConfirmText("");
      setShowConfirmation(false);
    }
  }, [isOpen, gameId]);

  const loadPreview = async () => {
    setIsLoadingPreview(true);
    try {
      const result = await apiService.getArchivePreview(gameId);
      if (result.success && result.data) {
        setPreviewData(result.data);
      } else {
        console.error('Failed to load preview:', result.error);
      }
    } catch (error) {
      console.error('Error loading preview:', error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const resetForm = () => {
    setArchiveSuffix("");
    setConfirmText("");
    setShowConfirmation(false);
    setPreviewData(null);
  };

  const handleClose = () => {
    if (!isArchiving) {
      resetForm();
      onClose();
    }
  };

  const handleProceed = () => {
    setShowConfirmation(true);
  };

  const handleArchiveGame = async () => {
    if (!isConfirmValid) {
      toast({
        title: "Error",
        description: `Please type "${expectedText}" to confirm`,
        variant: "destructive",
      });
      return;
    }

    setIsArchiving(true);
    try {
      const result = await apiService.archiveGame(gameId, archiveSuffix || undefined);

      if (result.success) {
        toast({
          title: "Game Archived",
          description: `Game "${gameName}" has been archived successfully as "${archiveName}"`,
        });
        resetForm();
        onClose();
        if (onGameArchived) {
          onGameArchived();
        }
      } else {
        toast({
          title: "Archive Failed",
          description: result.error || "Failed to archive game",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error archiving game:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while archiving the game",
        variant: "destructive",
      });
    } finally {
      setIsArchiving(false);
    }
  };

  if (showConfirmation) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-center flex items-center justify-center gap-2 text-amber-600">
              <Archive className="h-5 w-5" />
              Confirm Archive
            </DialogTitle>
            <DialogDescription className="text-center">
              Please review and confirm the archive operation
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-amber-500/10 border border-amber-500/20 rounded p-4">
              <p className="text-sm text-foreground font-medium mb-2">
                You are about to archive:
              </p>
              <p className="text-lg font-semibold text-amber-600">
                {gameName}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Archive name: <span className="font-mono">{archiveName}</span>
              </p>
            </div>

            {previewData && (
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-center font-medium mb-3">Data to be archived:</p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 bg-background rounded">
                    <p className="text-xl font-bold">{previewData.entities.documents.count}</p>
                    <p className="text-xs text-muted-foreground">Documents</p>
                  </div>
                  <div className="p-2 bg-background rounded">
                    <p className="text-xl font-bold">{previewData.entities.blueprints.count}</p>
                    <p className="text-xs text-muted-foreground">Blueprints</p>
                  </div>
                  <div className="p-2 bg-background rounded">
                    <p className="text-xl font-bold">{previewData.entities.libraryItems.count}</p>
                    <p className="text-xs text-muted-foreground">Library Items</p>
                  </div>
                  <div className="p-2 bg-background rounded">
                    <p className="text-xl font-bold">{previewData.entities.dialogs.count}</p>
                    <p className="text-xs text-muted-foreground">Dialogs</p>
                  </div>
                  <div className="p-2 bg-background rounded">
                    <p className="text-xl font-bold">{previewData.entities.images.count}</p>
                    <p className="text-xs text-muted-foreground">Images</p>
                  </div>
                  <div className="p-2 bg-background rounded">
                    <p className="text-xl font-bold">{previewData.entities.tmsStrings.count}</p>
                    <p className="text-xs text-muted-foreground">TMS Strings</p>
                  </div>
                </div>
                <p className="text-center text-xs text-muted-foreground mt-3">
                  Total: {previewData.summary.totalEntities} entities + {previewData.summary.totalTranslations} translations + {previewData.summary.totalImages} images
                </p>
              </div>
            )}

            <div className="bg-destructive/10 border border-destructive/20 rounded p-3">
              <p className="text-sm text-center text-destructive font-medium">
                Warning: The game will be removed after archiving. You can restore it from the Management Portal.
              </p>
            </div>

            <div>
              <label htmlFor="confirm-text" className="block text-sm font-medium text-foreground mb-2">
                Type <span className="font-mono font-bold">Archive {gameName}</span> to confirm *
              </label>
              <Input
                id="confirm-text"
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={expectedText}
                className="w-full"
                disabled={isArchiving}
                autoComplete="off"
              />
            </div>
          </div>

          <DialogFooter className="flex justify-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowConfirmation(false)}
              disabled={isArchiving}
            >
              Back
            </Button>
            <Button
              variant="default"
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleArchiveGame}
              disabled={isArchiving || !isConfirmValid}
            >
              {isArchiving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Archiving...
                </>
              ) : (
                <>
                  <Archive className="mr-2 h-4 w-4" />
                  Archive Game
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-center flex items-center justify-center gap-2 text-amber-600">
            <Archive className="h-5 w-5" />
            Archive Game
          </DialogTitle>
          <DialogDescription className="text-center">
            Archive "{gameName}" to safely store all its data. You can restore it later from the Management Portal.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 py-4 pr-2">
          {/* Archive Name */}
          <div className="space-y-2">
            <label className="text-sm font-medium block">Archive Name</label>
            <div className="p-3 bg-muted rounded-lg">
              <p className="font-mono text-sm break-all">{archiveName}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Add suffix (optional):</span>
              <Input
                value={archiveSuffix}
                onChange={(e) => setArchiveSuffix(e.target.value)}
                placeholder="e.g., backup, v1, final"
                className="flex-1 max-w-xs"
              />
            </div>
          </div>

          {/* Data Preview */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Data to be archived</label>
              {isLoadingPreview && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>

            {isLoadingPreview ? (
              <div className="p-8 bg-muted rounded-lg text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground mt-2">Loading preview...</p>
              </div>
            ) : previewData ? (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <EntityList
                    title="Documents"
                    count={previewData.entities.documents.count}
                    items={previewData.entities.documents.items}
                  />
                  <EntityList
                    title="Blueprints"
                    count={previewData.entities.blueprints.count}
                    items={previewData.entities.blueprints.items}
                  />
                  <EntityList
                    title="Library Items"
                    count={previewData.entities.libraryItems.count}
                    items={previewData.entities.libraryItems.items}
                  />
                  <EntityList
                    title="Dialogs"
                    count={previewData.entities.dialogs.count}
                    items={previewData.entities.dialogs.items}
                  />
                  <EntityList
                    title="Types"
                    count={previewData.entities.types.count}
                    items={previewData.entities.types.items}
                  />
                  <EntityList
                    title="Tags"
                    count={previewData.entities.tags.count}
                    items={previewData.entities.tags.items}
                  />
                  <EntityList
                    title="Logic Flows"
                    count={previewData.entities.logicFlows.count}
                    items={previewData.entities.logicFlows.items}
                  />
                  <EntityList
                    title="TMS Strings"
                    count={previewData.entities.tmsStrings.count}
                    items={previewData.entities.tmsStrings.items}
                  />
                  <EntityList
                    title="Translations"
                    count={previewData.entities.translations.count}
                    items={[]}
                  />
                  <EntityList
                    title="Images"
                    count={previewData.entities.images.count}
                    items={previewData.entities.images.items}
                  />
                </div>

                <div className="p-3 bg-muted rounded-lg text-center">
                  <p className="text-sm">
                    <span className="font-bold">{previewData.summary.totalEntities}</span> total entities
                    {previewData.summary.totalTranslations > 0 && (
                      <span> + <span className="font-bold">{previewData.summary.totalTranslations}</span> translations</span>
                    )}
                    {previewData.summary.totalImages > 0 && (
                      <span> + <span className="font-bold">{previewData.summary.totalImages}</span> images</span>
                    )}
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-center text-sm text-muted-foreground">
                  Failed to load preview data
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadPreview}
                  className="w-full mt-2"
                >
                  Retry
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex justify-center gap-2 pt-4 border-t">
          <Button variant="outline" onClick={handleClose} className="w-24">
            Cancel
          </Button>
          <Button
            onClick={handleProceed}
            className="w-24 bg-amber-600 hover:bg-amber-700"
            disabled={isLoadingPreview}
          >
            Next
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
